import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import GrainTwoToneIcon from '@mui/icons-material/GrainTwoTone';
import { getDataSources } from '../../services/llm_api_services';
import Loader from '../../common/components/Loader';

interface DataSource {
  name: string;
  type: 'Structured' | 'Unstructured';
}

interface DataSourceCategory {
  id: string;
  name: string;
  iconName: string;
  dataSources: DataSource[];
}

interface DataSourcesPageProps {
  diagram_id: string;
  organization_id: string;
  solution_id: string;
}

const DataSourcesPage: React.FC<DataSourcesPageProps> = ({
  diagram_id,
  organization_id,
  solution_id,
}) => {
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(new Set());
  const [internalDataSources, setInternalDataSources] = useState<DataSourceCategory[]>([]);
  const [externalDataSources, setExternalDataSources] = useState<DataSourceCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchDataSources = async () => {
      setLoading(true);
      try {
        const response = await getDataSources(diagram_id, organization_id);

        const internalCategories: DataSourceCategory[] = Object.entries(
          response.data_sources.internal
        ).map(([name, data], index) => ({
          id: `internal-${index}`,
          name,
          iconName: name,
          dataSources: [
            ...data.structured
              .filter((item) => item && item.trim() !== '')
              .map((item) => ({ name: item, type: 'Structured' as const })),
            ...data.unstructured
              .filter((item) => item && item.trim() !== '')
              .map((item) => ({ name: item, type: 'Unstructured' as const })),
          ],
        }));

        const externalCategories: DataSourceCategory[] = Object.entries(
          response.data_sources.external
        ).map(([name, data], index) => ({
          id: `external-${index}`,
          name,
          iconName: name,
          dataSources: [
            ...data.structured
              .filter((item) => item && item.trim() !== '')
              .map((item) => ({ name: item, type: 'Structured' as const })),
            ...data.unstructured
              .filter((item) => item && item.trim() !== '')
              .map((item) => ({ name: item, type: 'Unstructured' as const })),
          ],
        }));

        setInternalDataSources(internalCategories);
        setExternalDataSources(externalCategories);
      } catch (error) {
        console.error('Error fetching data sources:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDataSources();
  }, [diagram_id, organization_id, solution_id]);

  const getCompanyLogoSrc = (productName: string) => {
    if (!productName) return '';
    const allIcons = [
      'adobe.svg',
      'anaplan.svg',
      'atlassian.svg',
      'blue-yonder.svg',
      'CCH Tagetik.svg',
      'coupa.svg',
      'epicor.svg',
      'Groove.svg',
      'hubspot.svg',
      'IBM.svg',
      'ifs.svg',
      'infor.svg',
      'kinaxis.svg',
      'mailchimp.svg',
      'microsoft.svg',
      'mitratech.svg',
      'onestream.svg',
      'oracle.svg',
      'Outreach.svg',
      'planful.svg',
      'pega.svg',
      'sage.svg',
      'Salesforce.svg',
      'SAP.svg',
      'service-now.svg',
      'siemens.svg',
      'syncron.svg',
      'snowflake.svg',
      'uipath.svg',
      'Veeva.svg',
      'workday.svg',
      'xero.svg',
      'zoho.svg',
    ];

    const words = productName.split(/\s+/).map((w) => w.toLowerCase().replace(/[^a-z0-9]/g, ''));
    let found = '';

    for (const icon of allIcons) {
      const fileNoExt = icon
        .replace(/\.svg$/i, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      if (words.includes(fileNoExt)) {
        found = icon;
        break;
      }
    }

    if (!found) {
      for (const icon of allIcons) {
        const fileNoExt = icon
          .replace(/\.svg$/i, '')
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '');
        if (words.some((w) => w && fileNoExt.includes(w))) {
          found = icon;
          break;
        }
      }
    }

    return found ? `/images/company/${found}` : '';
  };

  const getProductInitials = (productName: string): string => {
    if (!productName) return '';
    const words = productName
      // eslint-disable-next-line no-useless-escape
      .split(/[\s\-_\.]+/)
      .filter((word) => word.length > 0)
      .map((word) => word.trim());

    if (words.length === 0) return '';
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  const handleImageError = (iconName: string) => {
    setImageLoadErrors((prev) => new Set(prev).add(iconName));
  };

  const handleImageLoad = (iconName: string) => {
    setImageLoadErrors((prev) => {
      const newSet = new Set(prev);
      newSet.delete(iconName);
      return newSet;
    });
  };

  const renderIcon = (iconName: string, displayName: string) => {
    if (iconName === 'Others') {
      return (
        <Box
          sx={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <GrainTwoToneIcon sx={{ fontSize: 24, color: '#5A6C7D' }} />
        </Box>
      );
    }

    const iconSrc = getCompanyLogoSrc(displayName);
    const hasError = imageLoadErrors.has(iconName);

    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {iconSrc && !hasError ? (
          <img
            key={`${iconName}-icon`}
            src={iconSrc}
            alt={displayName + ' icon'}
            style={{ width: 24, height: 24, objectFit: 'contain' }}
            onError={() => handleImageError(iconName)}
            onLoad={() => handleImageLoad(iconName)}
          />
        ) : (
          <Box
            sx={{
              width: 24,
              height: 24,
              background: 'linear-gradient(135deg, #BBDBFF 0%, #51A3FF 100%)',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '11px',
              fontWeight: '600',
              fontFamily: 'Poppins',
            }}
          >
            {getProductInitials(displayName)}
          </Box>
        )}
      </Box>
    );
  };

  const renderDataSourceCategory = (category: DataSourceCategory) => {
    if (category.dataSources.length === 0) return null;

    return (
      <Box
        key={category.id}
        sx={{
          background: '#FFFFFF',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, background: '#F9FAFB' }}
        >
          {renderIcon(category.iconName, category.name)}
          <Typography
            sx={{ fontFamily: 'Poppins', fontSize: 16, fontWeight: 500, color: '#5A6C7D' }}
          >
            {category.name}
          </Typography>
        </Box>

        <Box sx={{ p: 1.5 }}>
          {category.dataSources.map((source, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: '16px',
                borderBottom: idx < category.dataSources.length - 1 ? '1px solid #E8EBED' : 'none',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: 14,
                  color: '#1A1A1A',
                  fontWeight: 400,
                }}
              >
                {source.name}
              </Typography>
              <Chip
                label={source.type}
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '10px',
                  fontWeight: 400,
                  fontStyle: 'italic',
                  color: '#4D5358',
                  background: '#F9FAFB',
                  border: '0.5px solid #DDE1E6',
                  borderRadius: '12px',
                  height: '20px',
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}
        >
          <Loader message="Loading Data Sources..." />
        </Box>
      ) : (
        <>
          <Box>
            <Typography
              sx={{ fontFamily: 'Poppins', fontSize: 24, fontWeight: 500, color: '#1A1A1A', p: 2 }}
            >
              Data Sources
            </Typography>
          </Box>

          <Box sx={{ mb: 3, p: 2 }}>
            <Typography sx={{ fontFamily: 'Poppins', fontSize: 18, fontWeight: 600, mb: 2 }}>
              Internal Data Sources
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
              }}
            >
              {internalDataSources.map((category) => renderDataSourceCategory(category))}
            </Box>
          </Box>

          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontFamily: 'Poppins', fontSize: 18, fontWeight: 600, mb: 2 }}>
              External Data Sources
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
              }}
            >
              {externalDataSources.map((category) => renderDataSourceCategory(category))}
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default DataSourcesPage;
